import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Pill, Check, ShoppingCart, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api/client";

const inr = (n) => `₹${Number(n || 0).toFixed(0)}`;

const RefillDialog = ({ open, prescriptionId, onClose, onAddedToCart }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState({});
    const [qty, setQty] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !prescriptionId) return;
        let active = true;
        (async () => {
            setLoading(true);
            try {
                const r = await api.post(`/prescriptions/${prescriptionId}/refill`);
                if (!active) return;
                setData(r.data);
                // Preselect every matched item, qty 1
                const sel = {};
                const q = {};
                (r.data.matches || []).forEach((m, i) => {
                    if (m.matched_product) {
                        sel[i] = true;
                        q[i] = 1;
                    }
                });
                setSelected(sel);
                setQty(q);
            } catch (err) {
                const detail = err?.response?.data?.detail;
                toast.error(typeof detail === "string" ? detail : detail?.message || "Could not match medicines");
                onClose?.();
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [open, prescriptionId]); // eslint-disable-line

    if (!open) return null;

    const selectedCount = Object.values(selected).filter(Boolean).length;
    const lineTotal = (data?.matches || []).reduce((sum, m, i) => {
        if (!selected[i] || !m.matched_product) return sum;
        return sum + (m.matched_product.selling_price || 0) * (qty[i] || 1);
    }, 0);

    const submit = async () => {
        if (submitting || selectedCount === 0) return;
        setSubmitting(true);
        let added = 0;
        let failed = 0;
        for (let i = 0; i < (data?.matches || []).length; i++) {
            const m = data.matches[i];
            if (!selected[i] || !m.matched_product) continue;
            try {
                await api.post("/cart/items", {
                    product_id: m.matched_product.id,
                    qty: qty[i] || 1,
                    prescription_id: m.matched_product.prescription_required ? prescriptionId : undefined,
                });
                added += 1;
            } catch (err) {
                const code = err?.response?.data?.detail?.code;
                if (code === "PRESCRIPTION_REJECTED") {
                    toast.error("Prescription was rejected. Upload a fresh one to continue.");
                    failed += 1;
                    break;
                }
                failed += 1;
            }
        }
        setSubmitting(false);
        if (added > 0) {
            toast.success(`Added ${added} medicine${added > 1 ? "s" : ""} to cart${failed > 0 ? ` • ${failed} failed` : ""}`);
            onAddedToCart?.();
            onClose?.();
        } else {
            toast.error("Could not add any item to cart");
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/40 p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid="refill-dialog">
            <div className="w-full max-w-xl rounded-t-3xl border border-zinc-100 bg-white p-6 shadow-2xl dark:border-white/5 dark:bg-zinc-900 sm:rounded-3xl">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="label-uppercase">One-tap refill</p>
                        <h2 className="mt-1 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Refill your medicines</h2>
                        <p className="mt-1 text-sm text-zinc-500">Matched against our catalog. Review, adjust qty and add to cart.</p>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-white/10" aria-label="Close" data-testid="refill-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {loading ? (
                    <div className="mt-6 flex items-center justify-center py-10 text-sm text-zinc-500">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Matching medicines...
                    </div>
                ) : (
                    <>
                        <p className="mt-5 text-xs text-zinc-500" data-testid="refill-summary">
                            Matched <strong className="text-brand-700">{data.matched_count}</strong> of <strong>{data.total_medicines}</strong> medicines from this prescription.
                        </p>

                        <ul className="mt-3 max-h-[380px] space-y-2 overflow-y-auto pr-1">
                            {data.matches.map((m, i) => {
                                const mp = m.matched_product;
                                if (!mp) {
                                    return (
                                        <li key={i} className="rounded-2xl border border-zinc-100 bg-zinc-50/60 p-3 dark:border-white/5 dark:bg-white/5" data-testid={`refill-row-unmatched-${i}`}>
                                            <p className="flex items-center gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                                <Pill className="h-3.5 w-3.5 text-zinc-400" /> {m.medicine.name}
                                            </p>
                                            <p className="mt-1 text-xs text-zinc-500">No catalog match found. We'll restock soon.</p>
                                        </li>
                                    );
                                }
                                return (
                                    <li key={i} className={`rounded-2xl border p-3 transition-colors ${
                                        selected[i] ? "border-brand-300 bg-brand-50/40 dark:border-brand-400/40 dark:bg-brand-500/10" : "border-zinc-100 dark:border-white/5"
                                    }`} data-testid={`refill-row-${i}`}>
                                        <label className="flex cursor-pointer items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={!!selected[i]}
                                                onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                                                className="mt-1 h-4 w-4 accent-brand-700"
                                                data-testid={`refill-checkbox-${i}`}
                                            />
                                            <img src={mp.image || "/pills-logo.jpg"} alt="" className="h-12 w-12 rounded-lg object-cover ring-1 ring-zinc-200" />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs text-zinc-500">For: <em>{m.medicine.name}</em>{m.medicine.dosage && ` • ${m.medicine.dosage}`}</p>
                                                <p className="line-clamp-1 text-sm font-medium text-zinc-900 dark:text-zinc-50">{mp.name}</p>
                                                <p className="text-xs text-zinc-500">{mp.brand} • {mp.pack_size}</p>
                                                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                                                    <span className="font-semibold text-zinc-900 dark:text-zinc-100">{inr(mp.selling_price)}</span>
                                                    {mp.mrp > mp.selling_price && <span className="text-zinc-400 line-through">{inr(mp.mrp)}</span>}
                                                    {mp.prescription_required && (
                                                        <span className="inline-flex items-center gap-0.5 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                                                            <ShieldCheck className="h-2.5 w-2.5" /> Rx
                                                        </span>
                                                    )}
                                                    <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] text-zinc-500 dark:bg-white/10 dark:text-zinc-400">
                                                        match {Math.round(m.confidence * 100)}%
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.preventDefault(); setQty({ ...qty, [i]: Math.max(1, (qty[i] || 1) - 1) }); }}
                                                    className="rounded-md border border-zinc-200 px-2 py-0.5 text-xs hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5"
                                                    data-testid={`refill-qty-dec-${i}`}
                                                >−</button>
                                                <span className="min-w-[1.5rem] text-center text-xs font-medium" data-testid={`refill-qty-${i}`}>{qty[i] || 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.preventDefault(); setQty({ ...qty, [i]: Math.min(99, (qty[i] || 1) + 1) }); }}
                                                    className="rounded-md border border-zinc-200 px-2 py-0.5 text-xs hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5"
                                                    data-testid={`refill-qty-inc-${i}`}
                                                >+</button>
                                            </div>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="mt-5 rounded-2xl border border-brand-100 bg-brand-50/40 p-3 dark:border-brand-400/20 dark:bg-brand-500/10">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-zinc-700 dark:text-zinc-300">{selectedCount} item{selectedCount !== 1 ? "s" : ""} selected</span>
                                <span className="font-semibold text-zinc-900 dark:text-zinc-100" data-testid="refill-total">{inr(lineTotal)}</span>
                            </div>
                        </div>

                        <button
                            onClick={submit}
                            disabled={submitting || selectedCount === 0}
                            className="btn-brand mt-5 w-full"
                            data-testid="refill-add-to-cart"
                        >
                            {submitting ? "Adding..." : (<><ShoppingCart className="h-4 w-4" /> Add {selectedCount > 0 ? `${selectedCount} item${selectedCount > 1 ? "s" : ""}` : ""} to cart</>)}
                        </button>
                        <p className="mt-2 text-center text-[11px] text-zinc-500">
                            <Link to="/cart" className="text-brand-700 hover:underline">Go to cart</Link>{" "}— prices, GST and shipping computed at checkout.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default RefillDialog;
